import axios from 'axios';

class AuthService {
  constructor() {
    this._currentUser = null;
    this._isLoggedIn = false;
    this._listeners = [];
    this._hasKeychain = false;
    this._isMobile = false;

    // Cache für API-Token
    this._apiTokenCache = null;
    this._apiTokenExpiry = null;

    // Beim Start prüfen, ob Hive Keychain verfügbar ist
    this.checkKeychainAvailability();
    
    // Überprüfen, ob auf einem mobilen Gerät zugegriffen wird
    this.checkMobileDevice();

    // Prüfen, ob ein Benutzer bereits angemeldet ist (aus localStorage)
    this.checkStoredSession();
    
    // Event-Handler für die Rückkehr vom mobilen Keychain hinzufügen
    this.setupMobileReturnHandler();
    
    // Event-Handler für die mobile Keychain-Signatur
    if (typeof document !== 'undefined') {
      document.addEventListener('hivekeychainmobilesig', () => {
        this.handleMobileKeychainSignature();
      });
    }
  }
  
  // Handler für mobile Keychain-Signatur
  handleMobileKeychainSignature() {
    if (typeof window === 'undefined') return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const signature = urlParams.get('sig');
    
    if (signature && this._pendingLogin) {
      const { username, memo } = this._pendingLogin;
      
      // Signatur gefunden, Anmeldung erfolgreich
      this._currentUser = {
        username,
        memo,
        signature,
        timestamp: new Date().toISOString()
      };
      
      this._isLoggedIn = true;
      this._pendingLogin = null;
      
      // Benutzerdaten im localStorage speichern
      localStorage.setItem('hive_user', JSON.stringify(this._currentUser));
      localStorage.removeItem('hive_pending_auth');
      
      // Aufrufparameter aus URL entfernen
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Benutzerdaten laden
      this.loadUserData();
      
      // Listener über die Änderung informieren
      this._notifyListeners();
    }
  }
  
  // Prüfen, ob ein mobiles Gerät verwendet wird
  checkMobileDevice() {
    if (typeof window === 'undefined') {
      this._isMobile = false;
      return;
    }
    
    const userAgent = window.navigator.userAgent || window.navigator.vendor || window.opera;
    this._isMobile = /android|iPad|iPhone|iPod|webOS|BlackBerry|Windows Phone/i.test(userAgent);
    this._notifyListeners();
  }
  
  // Event-Handler für die Rückkehr von der mobilen Keychain-App
  setupMobileReturnHandler() {
    if (typeof window === 'undefined') return;
    
    // Überprüfen, ob es sich um eine Rückkehr von der mobilen App handelt
    window.addEventListener('focus', () => {
      if (!this._isLoggedIn && this._pendingLogin) {
        this.checkStoredSession();
        if (this._pendingLogin) {
          // Wenn die Anmeldung immer noch aussteht, ggf. einen Timeout setzen, 
          // um die Anmeldung abzubrechen
          setTimeout(() => {
            if (this._pendingLogin) {
              this._pendingLogin = null;
              this._notifyListeners();
            }
          }, 5000);
        }
      }
    });
  }

  // Improved check for Hive Keychain availability
  checkKeychainAvailability() {
    if (typeof window === 'undefined') {
      this._hasKeychain = false;
      return;
    }

    const checkInterval = setInterval(() => {
      this._hasKeychain = window.hive_keychain !== undefined;
      this._notifyListeners();

      // Stop checking once keychain is detected
      if (this._hasKeychain) {
        clearInterval(checkInterval);
      }
    }, 500);

    // Stop checking after 10 seconds to avoid infinite loops
    setTimeout(() => clearInterval(checkInterval), 10000);
  }

  // Prüfen, ob ein Benutzer bereits in localStorage gespeichert ist
  checkStoredSession() {
    try {
      const storedUser = localStorage.getItem('hive_user');
      
      if (storedUser) {
        this._currentUser = JSON.parse(storedUser);
        this._isLoggedIn = true;
        this._pendingLogin = null;
        
        // API Token Cache wiederherstellen
        this.restoreApiTokenCache();
        
        this._notifyListeners();
        this.loadUserData();
      }
      
      // Überprüfen, ob eine ausstehende Authentifizierung vorliegt
      const pendingAuth = localStorage.getItem('hive_pending_auth');
      if (pendingAuth && !this._isLoggedIn) {
        const authData = JSON.parse(pendingAuth);
        // Nur setzen, wenn nicht zu alt (maximal 10 Minuten)
        const authTime = new Date(authData.timestamp).getTime();
        const now = new Date().getTime();
        if (now - authTime < 10 * 60 * 1000) {
          this._pendingLogin = authData;
          this._notifyListeners();
        } else {
          // Zu alte Auth-Anfrage löschen
          localStorage.removeItem('hive_pending_auth');
        }
      }
    } catch (e) {
      console.error('Fehler beim Laden der gespeicherten Sitzung:', e);
    }
  }

  // Mit Hive Keychain anmelden - Nur noch Browser-Variante, Mobile wird nicht mehr unterstützt
  login(username) {
    return new Promise((resolve, reject) => {
      if (!username) {
        reject(new Error('Benutzername ist erforderlich'));
        return;
      }
      // Zufällige Nachricht für die Signierung generieren
      const memo = `Login to BurnBabyBurn: ${new Date().toISOString()}`;
      // Nur noch Desktop-Authentifizierung mit Browser-Erweiterung
      if (!this._hasKeychain) {
        reject(new Error('Hive Keychain ist nicht installiert'));
        return;
      }
      // Signierungsanfrage an Keychain senden
      window.hive_keychain.requestSignBuffer(username, memo, 'Posting', async (response) => {
        if (response.success) {
          // Erfolgreich angemeldet
          this._currentUser = {
            username,
            memo,
            signature: response.result,
            timestamp: new Date().toISOString(),
            authType: 'keychain' // Wir speichern die Authentifizierungsmethode
          };
          
          this._isLoggedIn = true;
          
          // Direkt beim Login auch den Account-Namen signieren und cachen
          try {
            console.log('Signiere Account-Namen für API-Zugriff beim Login...');
            const apiToken = await this.signAccountName();
            this._apiTokenCache = apiToken;
            this._apiTokenExpiry = Date.now() + 30 * 60 * 1000; // 30 Minuten Cache
            
            // Token auch in localStorage speichern
            this.saveApiTokenCache();
            
            console.log('API-Token erfolgreich gecacht');
          } catch (tokenError) {
            console.warn('Konnte API-Token beim Login nicht erstellen:', tokenError.message);
            // Login trotzdem fortsetzen
          }
          
          // Benutzerdaten im localStorage speichern
          localStorage.setItem('hive_user', JSON.stringify(this._currentUser));
          // Benutzerdaten laden
          this.loadUserData();
          // Listener über die Änderung informieren
          this._notifyListeners();
          resolve(this._currentUser);
        } else {
          // Fehler bei der Anmeldung
          reject(new Error(response.message || 'Anmeldung fehlgeschlagen'));
        }
      });
    });
  }
  
  // Login mit der mobilen Keychain-App (deaktiviert)
  loginWithMobileKeychain(username, memo) {
    return Promise.reject(new Error('Mobile Keychain Login wird nicht mehr unterstützt. Bitte nutze die Hive Keychain Browser-Erweiterung.'));
  }
  
  // Überprüft das Ergebnis der mobilen Authentifizierung - Erweitert mit authType
  async checkMobileAuthResult(username, memo) {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const signature = urlParams.get('sig');
      
      if (signature) {
        // Signatur gefunden, Anmeldung erfolgreich
        this._currentUser = {
          username,
          memo,
          signature,
          timestamp: new Date().toISOString(),
          authType: 'keychain_mobile'
        };
        
        this._isLoggedIn = true;
        this._pendingLogin = null;
        
        // Benutzerdaten im localStorage speichern
        localStorage.setItem('hive_user', JSON.stringify(this._currentUser));
        
        // Aufrufparameter aus URL entfernen
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Benutzerdaten laden
        await this.loadUserData();
        
        // Listener über die Änderung informieren
        this._notifyListeners();
        
        return this._currentUser;
      } else {
        // Keine Signatur gefunden, möglicherweise ist die Anmeldung noch nicht abgeschlossen
        throw new Error('Keine Signatur erhalten. Bitte den Vorgang in der Hive Keychain App abschließen.');
      }
    } catch (error) {
      throw new Error('Fehler bei der Überprüfung der mobilen Anmeldung: ' + error.message);
    }
  }

  // Login mit Splinterlands-Anmeldedaten (E-Mail und Passwort)
  loginWithSplinterlands(email, password) {
    return new Promise(async (resolve, reject) => {
      try {
        if (!email || !password) {
          reject(new Error('E-Mail und Passwort sind erforderlich'));
          return;
        }
        
        // Wir nutzen die Splinterlands API, um die Anmeldung durchzuführen
        const response = await axios.post('https://api2.splinterlands.com/players/login', {
          email: email,
          password: password
        });
        
        // Prüfen, ob die Antwort erfolgreich ist
        if (response.data && response.data.success) {
          const { name: username, token } = response.data;
          
          if (!username) {
            reject(new Error('Benutzername konnte nicht ermittelt werden'));
            return;
          }
          
          // Setzen der Anmeldeinformationen
          this._currentUser = {
            username,
            authType: 'splinterlands',
            token,
            timestamp: new Date().toISOString()
          };
          
          this._isLoggedIn = true;
          
          // Benutzerdaten im localStorage speichern
          localStorage.setItem('hive_user', JSON.stringify(this._currentUser));
          
          // Benutzerdaten laden
          this.loadUserData();
          
          // Listener über die Änderung informieren
          this._notifyListeners();
          
          resolve(this._currentUser);
        } else {
          // Fehler bei der Anmeldung
          reject(new Error(response.data.error || 'Anmeldung fehlgeschlagen'));
        }
      } catch (error) {
        // API-Fehler
        reject(new Error('Fehler bei der Splinterlands-Anmeldung: ' + 
          (error.response?.data?.error || error.message || 'Unbekannter Fehler')));
      }
    });
  }

  // Benutzer abmelden
  logout() {
    this._currentUser = null;
    this._isLoggedIn = false;
    this._apiTokenCache = null; // Cache leeren
    this._apiTokenExpiry = null;
    localStorage.removeItem('hive_user');
    localStorage.removeItem('hive_pending_auth');
    localStorage.removeItem('api_token_cache'); // API Token Cache leeren
    this._pendingLogin = null;
    this._notifyListeners();
  }

  // Benutzerdaten von der Hive API laden - Anpassung für Splinterlands-Authentifizierung
  async loadUserData() {
    if (!this._currentUser || !this._currentUser.username) {
      return;
    }

    try {
      // Wenn es sich um Splinterlands-Auth handelt und ein Token existiert
      if (this._currentUser.authType === 'splinterlands' && this._currentUser.token) {
        try {
          // Account-Daten von der Splinterlands API abrufen
          const response = await axios.get('https://api2.splinterlands.com/players/details', {
            params: {
              name: this._currentUser.username,
              token: this._currentUser.token
            }
          });

          if (response.data) {
            // Benutzerdaten aktualisieren
            this._currentUser = {
              ...this._currentUser,
              account: {
                ...this._currentUser.account,
                splinterlands: response.data
              }
            };

            // Aktualisierte Daten im localStorage speichern
            localStorage.setItem('hive_user', JSON.stringify(this._currentUser));
            // Listener über die Änderung informieren
            this._notifyListeners();
          }
        } catch (splinterlandsError) {
        }
      }

      // Bei allen Auth-Typen immer die Hive-Daten laden
      // Account-Daten von der Hive-API abrufen
      const response = await axios.post('https://api.hive.blog', {
        jsonrpc: '2.0',
        method: 'condenser_api.get_accounts',
        params: [[this._currentUser.username]],
        id: 1
      });

      if (response.data && response.data.result && response.data.result.length > 0) {
        const userData = response.data.result[0];
        
        // Benutzerdaten aktualisieren
        this._currentUser = {
          ...this._currentUser,
          account: {
            ...this._currentUser.account,
            ...userData
          }
        };

        // Aktualisierte Daten im localStorage speichern
        localStorage.setItem('hive_user', JSON.stringify(this._currentUser));
        // Listener über die Änderung informieren
        this._notifyListeners();
      }
    } catch (error) {
    }
  }

  // Listener für Änderungen registrieren
  subscribe(callback) {
    this._listeners.push(callback);
    
    // Sofort den aktuellen Status mitteilen
    callback({
      hasKeychain: this._hasKeychain,
      isLoggedIn: this._isLoggedIn,
      currentUser: this._currentUser,
      isMobile: this._isMobile,
      pendingLogin: this._pendingLogin
    });
    
    // Unsubscribe-Funktion zurückgeben
    return () => {
      this._listeners = this._listeners.filter(listener => listener !== callback);
    };
  }

  // Alle Listener benachrichtigen
  _notifyListeners() {
    const data = {
      hasKeychain: this._hasKeychain,
      isLoggedIn: this._isLoggedIn,
      currentUser: this._currentUser,
      isMobile: this._isMobile,
      pendingLogin: this._pendingLogin
    };
    
    this._listeners.forEach(callback => callback(data));
  }

  // Getter für aktuelle Daten
  isLoggedIn() {
    return this._isLoggedIn;
  }

  hasKeychain() {
    return this._hasKeychain;
  }
  
  isMobile() {
    return this._isMobile;
  }
  
  hasPendingLogin() {
    return this._pendingLogin !== null;
  }

  getCurrentUser() {
    return this._currentUser;
  }

  // Testfunktion für die mobile Keychain-Verbindung (deaktiviert)
  testMobileKeychainConnection() {
    return Promise.reject(new Error('Mobile Keychain Verbindungstest wird nicht mehr unterstützt.'));
  }

  // Account-Name signieren für API-Authentifizierung
  signAccountName(accountName = null) {
    return new Promise((resolve, reject) => {
      const username = accountName || this._currentUser?.username;
      
      if (!username) {
        reject(new Error('No username available'));
        return;
      }

      // Bei Splinterlands-Auth können wir das bestehende Token verwenden
      if (this._currentUser?.authType === 'splinterlands' && this._currentUser?.token) {
        resolve(this._currentUser.token);
        return;
      }

      // Bei Keychain-Auth müssen wir den Account-Namen signieren
      if (!this._hasKeychain) {
        reject(new Error('Hive Keychain is not installed'));
        return;
      }

      // Account-Namen mit Posting Key signieren
      window.hive_keychain.requestSignBuffer(username, username, 'Posting', (response) => {
        if (response.success) {
          resolve(response.result);
        } else {
          // Bessere Fehlerbehandlung für verschiedene Keychain-Fehler
          if (response.message && response.message.includes('User ignored')) {
            reject(new Error('Authentication was cancelled by user'));
          } else if (response.message && response.message.includes('Invalid key')) {
            reject(new Error('Invalid posting key'));
          } else if (response.message && response.message.includes('User denied')) {
            reject(new Error('Authentication was denied by user'));
          } else {
            reject(new Error(response.message || 'Authentication failed'));
          }
        }
      });
    });
  }

  // Hilfsfunktion um ein Token für API-Requests zu erhalten
  async getApiToken(forceRefresh = false) {
    if (!this._isLoggedIn || !this._currentUser) {
      throw new Error('User is not logged in');
    }

    try {
      // Bei Splinterlands-Auth das bestehende Token verwenden
      if (this._currentUser.authType === 'splinterlands' && this._currentUser.token) {
        return this._currentUser.token;
      }

      // Überprüfen, ob ein gültiger Token im Cache vorhanden ist
      if (!forceRefresh && this._apiTokenCache && this._apiTokenExpiry && this._apiTokenExpiry > Date.now()) {
        return this._apiTokenCache;
      }

      // SplTrader API braucht spezifisch den signierten Account-Namen
      const token = await this.signAccountName();
      
      // Token im Cache speichern (gültig für 30 Minuten)
      this._apiTokenCache = token;
      this._apiTokenExpiry = Date.now() + 30 * 60 * 1000;
      
      // Token auch in localStorage speichern
      this.saveApiTokenCache();
      
      return token;
    } catch (error) {
      // Enhanced error handling for token generation
      this._apiTokenCache = null;
      this._apiTokenExpiry = null;
      
      // Provide more specific error messages based on the error type
      if (error.message.includes('cancelled') || error.message.includes('denied')) {
        throw new Error('Authentication cancelled by user');
      } else if (error.message.includes('Keychain')) {
        throw new Error('Hive Keychain error - please check if the extension is installed and working');
      } else {
        throw new Error('Error creating API token: ' + error.message);
      }
    }
  }

  // Cache leeren (für Fehlerbehandlung)
  clearApiTokenCache() {
    this._apiTokenCache = null;
    this._apiTokenExpiry = null;
    localStorage.removeItem('api_token_cache');
  }

  // Cache-Status überprüfen
  hasValidApiTokenCache() {
    return this._apiTokenCache && this._apiTokenExpiry && this._apiTokenExpiry > Date.now();
  }

  // Token-Cache erneuern
  async refreshApiToken() {
    return await this.getApiToken(true);
  }

  // Stellt sicher, dass wir einen gültigen API-Token haben
  async ensureValidApiToken() {
    if (!this.isLoggedIn()) {
      throw new Error('Authentication required');
    }

    // Prüfen, ob wir bereits einen gültigen Token haben
    if (this._apiTokenCache && this._apiTokenExpiry) {
      // Wenn der Token in den nächsten 5 Minuten abläuft, erneuern wir ihn proaktiv
      const fiveMinutesFromNow = Date.now() + (5 * 60 * 1000);
      
      if (this._apiTokenExpiry > fiveMinutesFromNow) {
        // Token ist noch lange gültig
        return this._apiTokenCache;
      }
    }

    // Token ist abgelaufen oder läuft bald ab, erneuern
    try {
      const newToken = await this.getApiToken(true); // Force refresh
      return newToken;
    } catch (error) {
      // Token-Refresh fehlgeschlagen
      this.clearApiTokenCache();
      throw new Error('Token refresh needed - please wait while we refresh your authentication');
    }
  }

  // API Token Cache in localStorage speichern
  saveApiTokenCache() {
    if (this._apiTokenCache && this._apiTokenExpiry) {
      const cacheData = {
        token: this._apiTokenCache,
        expiry: this._apiTokenExpiry,
        username: this._currentUser?.username
      };
      localStorage.setItem('api_token_cache', JSON.stringify(cacheData));
    }
  }

  // API Token Cache aus localStorage wiederherstellen
  restoreApiTokenCache() {
    try {
      const cacheData = localStorage.getItem('api_token_cache');
      if (cacheData) {
        const { token, expiry, username } = JSON.parse(cacheData);
        
        // Überprüfen, ob der Cache noch gültig ist und zum aktuellen Benutzer gehört
        if (expiry > Date.now() && username === this._currentUser?.username) {
          this._apiTokenCache = token;
          this._apiTokenExpiry = expiry;
        } else {
          // Abgelaufenen oder ungültigen Cache löschen
          localStorage.removeItem('api_token_cache');
        }
      }
    } catch (error) {
      localStorage.removeItem('api_token_cache');
    }
  }
}

// Singleton-Instanz exportieren
const authService = new AuthService();
export default authService;